/**
 * Whether we know who somebody is.
 *
 * The same question the server asks in PersonName, asked here so a screen can decide what to show
 * before a request is refused. The server is the one that enforces it; this only decides whether to
 * put a form in front of somebody rather than let them walk into a 400.
 *
 * Whitespace is not a name. The records this exists for carry two empty strings, and a space typed
 * into a field to get past a check would be the same record wearing a hat.
 */

/** The least a record needs for the question to be answerable. */
export interface NamedPerson {
  givenName?: string | null;
  familyName?: string | null;
}

/**
 * Whether both halves of a name are present.
 * @param person - The person, or nothing.
 * @returns True when both names are there.
 */
export const isCompleteName = (person?: NamedPerson | null): boolean =>
  !!person && !!(person.givenName ?? '').trim() && !!(person.familyName ?? '').trim();

/**
 * Whether this person has to be asked for their name before they go any further.
 *
 * Separate from isCompleteName because the answer is "no" while we do not yet know who they are:
 * a screen must not flash a name form at somebody whose record has simply not loaded.
 * @param person - The person, or nothing while loading.
 * @returns True only when the record is loaded and the name is incomplete.
 */
export const needsName = (person?: NamedPerson | null): boolean => !!person && !isCompleteName(person);
